import { endpoints } from '@/api/endpoints.ts'
import { axiosAPI } from '@/api/repository/repository.ts'
import { DeviceOptions, SilentModeSlot } from '@/model/device-options.ts'
import { Device } from '@/model/device.ts'
import { Message } from '@/model/message.ts'

function mapDevice (d: any): Device {
  return new Device(d.id, d.name, d.mac_address, d.user_id, d.last_seen, d.firmware_version, d.used_storage_percent, d.family_groups)
}

function mapDeviceOptions (d: any): DeviceOptions {
  const slots = (d.silent_mode ?? []).map((s: any) => new SilentModeSlot(s.start_hour, s.start_minute, s.end_hour, s.end_minute))
  return new DeviceOptions(d.volume_percent, d.time_seq_enabled, slots, d.dirty)
}

class DeviceRepository {
  async list (): Promise<Device[]> {
    const response = await axiosAPI.get(`${endpoints.device}`)
    return response.data.map((d: any) => mapDevice(d))
  }

  async getDevice (deviceId: string): Promise<Device> {
    const response = await axiosAPI.get(`${endpoints.device}/${deviceId}`)
    return mapDevice(response.data)
  }

  async updateDevice (deviceId: string, data: { name: string, family_groups: string[] }): Promise<Device> {
    const response = await axiosAPI.put(`${endpoints.device}/${deviceId}`, data)
    return mapDevice(response.data)
  }

  async reset (deviceId: string): Promise<void> {
    await axiosAPI.post(`${endpoints.device}/${deviceId}/reset`)
  }

  async upload (deviceId: string, audioBlob: Blob, expiresInDays: number): Promise<string> {
    const formData = new FormData()

    // Use standard multipart form-data for file upload
    formData.append('audio', audioBlob, 'recording.mp3')
    formData.append('expires_in_days', expiresInDays.toString())

    const response = await axiosAPI.post<{ id: string }>(
      `${endpoints.device}/${deviceId}/messages`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return response.data.id
  }

  async getOptions (deviceId: string): Promise<DeviceOptions> {
    const response = await axiosAPI.get(`${endpoints.device}/${deviceId}/options`)
    return mapDeviceOptions(response.data)
  }

  async updateOptions (deviceId: string, options: DeviceOptions): Promise<DeviceOptions> {
    const response = await axiosAPI.put(`${endpoints.device}/${deviceId}/options`, options)
    return mapDeviceOptions(response.data)
  }

  async listMessages (deviceId: string): Promise<Message[]> {
    const response = await axiosAPI.get(`${endpoints.device}/${deviceId}/messages`)
    return response.data.map((m: any) => new Message(
      m.id,
      m.device_id,
      new Date(m.created_at),
      new Date(m.expires_at),
      m.size,
      m.used_storage_percent ?? 0,
      m.is_mine,
      m.to_delete,
      m.play_count,
      m.downloaded_at ? new Date(m.downloaded_at) : undefined,
      m.first_played_at ? new Date(m.first_played_at) : undefined,
      m.last_played_at ? new Date(m.last_played_at) : undefined,
    ))
  }

  async deleteMessage (messageId: string): Promise<void> {
    await axiosAPI.post(`${endpoints.message}/${messageId}/delete`)
  }
}

export const deviceRepository = new DeviceRepository()
